// Banner.jsx (updated)
// --------------------
// Replace your current file contents with this (or merge the changes).

import { useQuery } from 'react-query'
import { deleteBanner, fetchBannerList } from './api'
import { PageTitle } from '../../../_metronic/layout/core'
import { Error500 } from '../../modules/errors/components/Error500'
import CRUDTable from '../../../components/table/CRUDTable'
import BannerForm from './Form'
import toast, { Toaster } from 'react-hot-toast'
import ModalWithForm from '../../../components/ModalWithForm'
import { Button } from 'react-bootstrap'
import viewProps from '../viewProps'
import { Processing } from '../../../components/Loaders'
import { Helmet } from 'react-helmet'
import ActivePill from '../../../components/ActivePill'
import InactivePill from '../../../components/InactivePill'
import DeleteLabel from '../../../components/DeleteLabel'
import DeleteConfirmationModal from '../../../components/DeleteWarning'
import EditLabel from '../../../components/EditLabel'
import PortalDropdown from '../../../components/PortalDropdown'
import TopBarProgress from 'react-topbar-progress-indicator'

// 🟢 NEW - helper to detect if a URL or filename is a video
const isVideoUrl = (url = '') => {
  if (!url || typeof url !== 'string') return false
  // strip query string, check extension (add more extensions if needed)
  const path = url.split('?')[0].toLowerCase()
  return /\.(mp4|webm|ogg|m4v|mov)$/i.test(path)
}

// 🟢 NEW - helper to detect if a URL is image
const isImageUrl = (url = '') => {
  if (!url || typeof url !== 'string') return false
  const path = url.split('?')[0].toLowerCase()
  return /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(path)
}

const bannerBreadcrumbs = [
  {
    title: 'School Management',
    path: 'school-management/banner',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

function Banner() {
  const { data, isError, isFetching, isLoading, refetch } = useQuery(
    'bannerList',
    fetchBannerList,
    {
      refetchOnWindowFocus: false,
    }
  )

  if (isLoading) {
    return <Processing />
  }

  const cellModifier = {
    banner_status: ({ value }) => {
      switch (Number(value)) {
        case 1:
          return <ActivePill />
        case 0:
          return <InactivePill />
        default:
          return ''
      }
    },

    // 🟢 NEW - Render banner_image as image or small video preview
    // This assumes the table column key is exactly "banner_image"
    banner_image: ({ value }) => {
      const url = value || ''
      const previewStyle = { maxWidth: 160, maxHeight: 90, objectFit: 'cover', borderRadius: 6 }

      if (!url) {
        return <span className="text-muted">—</span>
      }

      // video preview
      if (isVideoUrl(url)) {
        return (
          <video
            src={url}
            style={{ maxWidth: 160, maxHeight: 90, borderRadius: 6 }}
            controls={false}
            muted
            loop
            playsInline
            preload="metadata"
            onMouseEnter={(e) => e.currentTarget.play()}
            onMouseLeave={(e) => {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0
            }}
          />
        )
      }

      // image preview
      if (isImageUrl(url)) {
        return <img src={url} alt="banner" style={previewStyle} loading="lazy" />
      }

      // fallback: try to render image; if it fails the browser will show broken image
      return <img src={url} alt="banner" style={previewStyle} loading="lazy" />
    },

    Actions: ({ row }) => {
      return (
        <PortalDropdown triggerProps={{ className: 'btn btn-secondary px-0 py-1' }}>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={'Update Banner'}
              initialValues={row.original}
              onSubmit={refetch}
              Form={BannerForm}
              modalAttrs={{ size: 'md' }}
              triggerClassName="d-block"
            >
              <EditLabel />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <DeleteConfirmationModal
              triggerClassName="d-block"
              onDelete={async () => {
                const toastId = toast.loading('Deleting Please wait...')
                try {
                  const response = await deleteBanner(row.original.banner_id)
                  if (response?.status === false && response?.message) {
                    toast.error(response.message, { id: toastId })
                    return null
                  }
                  await refetch()
                  toast.success('Deleted Successfully', { id: toastId })
                } catch (error) {
                  toast.error('Something went wrong, please try again later', { id: toastId })
                }
              }}
            >
              <DeleteLabel />
            </DeleteConfirmationModal>
          </span>
        </PortalDropdown>
      )
    },
  }

  return (
    <>
      <Helmet>
        <title>TrackNAB | Banners</title>
      </Helmet>
      <PageTitle breadcrumbs={bannerBreadcrumbs}>Banner</PageTitle>
      {isError ? (
        <div className="text-center">
          <Error500 />
        </div>
      ) : (
        <CRUDTable
          data={data?.data || []}
          {...viewProps.Banner}
          fieldToLabelMap={viewProps.Banner.primaryFieldsToLabelMap}
          Form={BannerForm}
          formSize={{ size: 'md' }}
          cellModifier={cellModifier}
          nonSortableColumns={['Actions']}
          headerExtras={
            <ModalWithForm
              modalTitle={'Add Banner'}
              triggerClassName="ms-auto"
              onSubmit={refetch}
              Form={BannerForm}
              modalAttrs={{ size: 'md' }}
            >
              <Button size="sm">Add Banner</Button>
            </ModalWithForm>
          }
        />
      )}
      {isFetching && <TopBarProgress />}

      <Toaster />
    </>
  )
}

export default Banner
